/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/set-state-in-effect */
/**
 * TenantContext - Multi-Tenant Context Provider
 *
 * Provides tenant context throughout the application:
 * - Resolves current tenant from subdomain or localStorage
 * - Caches tenant info for performance
 * - Enables tenant switching for multi-tenant users
 * - Integrates with API layer for X-Tenant-ID header
 *
 * Similar pattern to AuthContext:
 * - Separate value and action contexts for performance
 * - Memoized values to prevent unnecessary re-renders
 * - Cache integration for zero API calls on subsequent loads
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import { request, registerPlatformAdminOverrideTenantId } from '../services/api/core'
import { logger } from '../utils/logger'
import { queryClient } from '../lib/react-query'
import type { Tenant, TenantContextValue } from '../types/tenant'

// Storage keys
const TENANT_ID_KEY = 'tagp-tenant-id'
const TENANT_DATA_KEY = 'tagp-tenant-data'
// Set when a platform admin picks the "All Tenants" aggregate view, so that
// choice survives a refresh instead of falling back to the last cached tenant.
const TENANT_ALL_KEY = 'tagp-tenant-all'
// User the cached selection belongs to — a selection is only restored for the
// user who made it, so the next person on this browser starts from their own.
const TENANT_OWNER_KEY = 'tagp-tenant-owner'

// Default context value
const defaultContextValue: TenantContextValue = {
  currentTenant: null,
  homeTenant: null,
  tenants: [],
  isLoading: true,
  hasMultipleTenants: false,
  isPlatformAdmin: false,
  switchTenant: async () => {},
  refreshTenants: async () => {},
  getTenantId: () => null,
}

// Create context
const TenantContext = createContext<TenantContextValue>(defaultContextValue)

// =============================================================================
// TENANT ID RESOLUTION FUNCTIONS
// =============================================================================

/**
 * Extract tenant slug from subdomain.
 * e.g., "northam.tagp.com" → "northam"
 */
function getTenantSlugFromSubdomain(): string | null {
  const hostname = window.location.hostname

  // Skip for localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null
  }

  const parts = hostname.split('.')

  // Minimum: subdomain.domain.tld (3 parts)
  if (parts.length >= 3) {
    const subdomain = parts[0]

    // Skip common non-tenant subdomains
    const reserved = ['www', 'api', 'admin', 'app', 'mail', 'ftp', 'static', 'assets']
    if (reserved.includes(subdomain.toLowerCase())) {
      return null
    }

    return subdomain.toLowerCase()
  }

  return null
}

/**
 * Get cached tenant ID from localStorage.
 */
function getCachedTenantId(): string | null {
  try {
    return localStorage.getItem(TENANT_ID_KEY)
  } catch {
    return null
  }
}

/**
 * Get cached tenant data from localStorage.
 */
function getCachedTenantData(): Tenant | null {
  try {
    const data = localStorage.getItem(TENANT_DATA_KEY)
    if (data) {
      return JSON.parse(data) as Tenant
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

/**
 * Cache tenant data in localStorage.
 * Selecting a concrete tenant always clears the "All Tenants" marker.
 */
function cacheTenantData(tenant: Tenant | null): void {
  try {
    localStorage.removeItem(TENANT_ALL_KEY)
    if (tenant) {
      localStorage.setItem(TENANT_ID_KEY, tenant.id)
      localStorage.setItem(TENANT_DATA_KEY, JSON.stringify(tenant))
    } else {
      localStorage.removeItem(TENANT_ID_KEY)
      localStorage.removeItem(TENANT_DATA_KEY)
    }
  } catch (e) {
    logger.warn('Failed to cache tenant data', e)
  }
}

/**
 * Whether the platform admin last selected the "All Tenants" aggregate view.
 */
function isAllTenantsCached(): boolean {
  try {
    return localStorage.getItem(TENANT_ALL_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Remember the "All Tenants" aggregate selection across reloads.
 */
function cacheAllTenantsSelection(): void {
  try {
    localStorage.setItem(TENANT_ALL_KEY, '1')
  } catch (e) {
    logger.warn('Failed to cache tenant selection', e)
  }
}

/**
 * Tag the cached selection with the user who made it.
 */
function setCachedSelectionOwner(userId: string | null | undefined): void {
  try {
    if (userId) {
      localStorage.setItem(TENANT_OWNER_KEY, userId)
    } else {
      localStorage.removeItem(TENANT_OWNER_KEY)
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Whether the cached selection was made by the given user.
 */
function isCachedSelectionOwnedBy(userId: string | null | undefined): boolean {
  if (!userId) return false
  try {
    return localStorage.getItem(TENANT_OWNER_KEY) === userId
  } catch {
    return false
  }
}

// =============================================================================
// GLOBAL TENANT ID GETTER (for API layer)
// =============================================================================

// Store reference to current tenant ID for API layer
let currentTenantIdRef: string | null = null
let isPlatformAdminRef = false

/**
 * Get current tenant ID for API calls.
 * This is called by the API layer to add X-Tenant-ID header.
 */
export function getCurrentTenantId(): string | null {
  if (currentTenantIdRef) {
    return currentTenantIdRef
  }

  const subdomainSlug = getTenantSlugFromSubdomain()
  if (subdomainSlug) {
    return subdomainSlug
  }

  return getCachedTenantId()
}

/**
 * Returns the tenant ID to send as X-Tenant-ID for platform admin tenant
 * context (selected tenant in the header switcher). Called by request().
 */
export function getPlatformAdminOverrideTenantId(): string | null {
  if (!isPlatformAdminRef || !currentTenantIdRef) return null
  return currentTenantIdRef
}

registerPlatformAdminOverrideTenantId(getPlatformAdminOverrideTenantId)

// =============================================================================
// TENANT PROVIDER
// =============================================================================

interface TenantProviderProps {
  children: ReactNode
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { currentUser, isAuthenticated, isCheckingAuth } = useAuth()

  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)
  const [homeTenant, setHomeTenant] = useState<Tenant | null>(null)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)

  /**
   * Fetch tenant info from API.
   */
  const fetchTenantInfo = useCallback(async (): Promise<{
    tenant: Tenant | null
    accessibleTenants: Tenant[]
    isPlatformAdmin: boolean
  }> => {
    try {
      const response = await request<{
        tenant?: Tenant
        accessible_tenants?: Tenant[]
        is_platform_admin?: boolean
      }>('/auth/me/tenant')

      return {
        tenant: response.tenant || null,
        accessibleTenants: response.accessible_tenants || [],
        isPlatformAdmin: response.is_platform_admin || false,
      }
    } catch (error) {
      logger.debug('Tenant info endpoint not available, using fallback', error)
      const cached = getCachedTenantData()
      return {
        tenant: cached,
        accessibleTenants: cached ? [cached] : [],
        isPlatformAdmin: false,
      }
    }
  }, [])

  /**
   * Load tenant context on mount and auth change.
   */
  const loadTenantContext = useCallback(async () => {
    // Wait for auth check to complete
    if (isCheckingAuth) {
      return
    }

    if (!isAuthenticated || !currentUser) {
      setCurrentTenant(null)
      setHomeTenant(null)
      setTenants([])
      setIsPlatformAdmin(false)
      setIsLoading(false)
      currentTenantIdRef = null
      isPlatformAdminRef = false
      return
    }

    setIsLoading(true)

    try {
      const { tenant: apiTenant, accessibleTenants, isPlatformAdmin: isAdmin } =
        await fetchTenantInfo()

      setIsPlatformAdmin(isAdmin)
      isPlatformAdminRef = isAdmin

      if (apiTenant) {
        setHomeTenant(apiTenant)

        // Only this user's own selection is restored (the cache is shared per browser).
        const hasOwnSelection = isCachedSelectionOwnedBy(currentUser.id)

        // Platform admin who was viewing the aggregate: keep them there.
        if (isAdmin && hasOwnSelection && isAllTenantsCached()) {
          setCurrentTenant(null)
          setTenants(accessibleTenants.length > 0 ? accessibleTenants : [apiTenant])
          currentTenantIdRef = null
          return
        }

        let activeTenant = apiTenant
        const cachedTenant = hasOwnSelection ? getCachedTenantData() : null

        if (cachedTenant && cachedTenant.id !== apiTenant.id) {
          // Platform admins switch tenants from the full platform list, which is
          // not part of accessible_tenants (that only carries their home tenant),
          // so their last selection is restored as-is. Regular users are still
          // restricted to tenants the API says they can access.
          if (isAdmin || accessibleTenants.some((t) => t.id === cachedTenant.id)) {
            activeTenant = cachedTenant
          }
        }

        const tenantList = accessibleTenants.length > 0 ? accessibleTenants : [activeTenant]

        setCurrentTenant(activeTenant)
        setTenants(
          tenantList.some((t) => t.id === activeTenant.id)
            ? tenantList
            : [activeTenant, ...tenantList]
        )
        currentTenantIdRef = activeTenant.id
        cacheTenantData(activeTenant)
        setCachedSelectionOwner(currentUser.id)
        return
      }

      // Subdomain fallback
      const subdomainSlug = getTenantSlugFromSubdomain()
      const cachedTenant = getCachedTenantData()

      if (cachedTenant) {
        setCurrentTenant(cachedTenant)
        setTenants([cachedTenant])
        currentTenantIdRef = cachedTenant.id
      } else if (subdomainSlug) {
        const minimalTenant: Tenant = {
          id: subdomainSlug,
          name: subdomainSlug,
          slug: subdomainSlug,
          description: null,
          plan: 'enterprise',
          status: 'active',
          owner_id: null,
          settings: null,
          quotas: null,
          created_at: 0,
          updated_at: 0,
          created_by: null,
          updated_by: null,
        }
        setCurrentTenant(minimalTenant)
        currentTenantIdRef = subdomainSlug
      }
    } catch (error) {
      logger.error('Failed to load tenant context', error)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, isCheckingAuth, currentUser, fetchTenantInfo])

  const switchTenant = useCallback(async (tenantId: string, tenantObject?: Tenant) => {
    if (!isPlatformAdmin) {
      logger.warn('Tenant switching is only available for platform admins.')
      return
    }

    if (tenantId === 'all') {
      setCurrentTenant(null)
      currentTenantIdRef = null
      cacheAllTenantsSelection()
      setCachedSelectionOwner(currentUser?.id)
      queryClient.clear()
      return
    }

    // Accept a directly-provided tenant object (e.g. from platform admin full list)
    // or fall back to searching the accessible tenants list
    const target = tenantObject ?? tenants.find((t) => t.id === tenantId)
    if (!target) {
      logger.warn('Target tenant not found', { tenantId })
      return
    }

    setCurrentTenant(target)
    currentTenantIdRef = target.id
    cacheTenantData(target)
    setCachedSelectionOwner(currentUser?.id)
    queryClient.clear()
  }, [isPlatformAdmin, tenants, currentUser])

  /**
   * Refresh tenant data from API.
   */
  const refreshTenants = useCallback(async () => {
    setIsLoading(true)
    await loadTenantContext()
  }, [loadTenantContext])

  /**
   * Get current tenant ID (for external use).
   */
  const getTenantId = useCallback((): string | null => {
    return currentTenant?.id || getCurrentTenantId()
  }, [currentTenant])

  // Load tenant on mount and when auth changes
  useEffect(() => {
    loadTenantContext()
  }, [loadTenantContext])

  // Memoize context value
  const contextValue = useMemo<TenantContextValue>(
    () => ({
      currentTenant,
      homeTenant,
      tenants,
      isLoading,
      hasMultipleTenants: tenants.length > 1,
      isPlatformAdmin,
      switchTenant,
      refreshTenants,
      getTenantId,
    }),
    [currentTenant, homeTenant, tenants, isLoading, isPlatformAdmin, switchTenant, refreshTenants, getTenantId]
  )

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access tenant context.
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

/**
 * Hook to get just the current tenant.
 */
export function useCurrentTenant(): Tenant | null {
  const { currentTenant } = useTenant()
  return currentTenant
}

/**
 * Hook to check if tenant is loading.
 */
export function useIsTenantLoading(): boolean {
  const { isLoading } = useTenant()
  return isLoading
}

/**
 * Hook to check if current user is a Platform Admin.
 */
export function useIsPlatformAdmin(): boolean {
  const { isPlatformAdmin } = useTenant()
  return isPlatformAdmin
}
